import { SERVER_ERROR_MESSAGE } from "./apiError";
import { resolveApiBaseUrl } from "./apiBase";

interface AdminNotificationResponse {
  success: boolean;
  message?: string;
  [key: string]: unknown;
}

function getAuthToken(): string | null {
  return localStorage.getItem("idToken") || localStorage.getItem("authToken");
}

export const sendAdminNotification = async (
  info: string
): Promise<AdminNotificationResponse | null> => {
  try {
    const apiPath = resolveApiBaseUrl();
    const token = getAuthToken();
    const key = import.meta.env.VITE_LABWICK_TOKEN || "";

    if (!apiPath) {
      console.error("API path is missing.");
      return null;
    }
    if (!token && !key) {
      console.error("No authentication token or key available.");
      return null;
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    } else {
      headers["key"] = key;
    }

    const response = await fetch(`${apiPath}/email/notify/newuser`, {
      method: "POST",
      headers,
      body: JSON.stringify({ info }),
    });

    if (response.status === 401) {
      localStorage.removeItem("idToken");
      localStorage.removeItem("authToken");
    }

    if (!response.ok) {
      console.error("Failed to send admin notification:", response.statusText);
      if (response.status >= 500) {
        return {
          success: false,
          message: SERVER_ERROR_MESSAGE,
        };
      }
      return {
        success: false,
        message: `Request failed with status ${response.status}: ${response.statusText}`,
      };
    }

    const data: AdminNotificationResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to send admin notification:", error);
    return {
      success: false,
      message: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
    };
  }
};

export interface GetInTouchPayload {
  email: string;
  turnstileToken: string;
  website?: string;
  name?: string;
  organization?: string;
  source?: string;
}

export interface GetInTouchResponse {
  success: boolean;
  message?: string;
  [key: string]: unknown;
}

export async function fetchContactCaptcha(): Promise<
  { ok: true; siteKey: string } | { ok: false; message: string }
> {
  try {
    const apiPath = resolveApiBaseUrl();
    if (!apiPath) {
      return { ok: false, message: "API URL is not configured." };
    }
    const response = await fetch(`${apiPath}/contact/captcha`, {
      cache: "no-store",
    });
    const data = await response.json().catch(() => null);
    if (
      !response.ok ||
      data?.provider !== "turnstile" ||
      typeof data?.siteKey !== "string" ||
      !data.siteKey
    ) {
      return {
        ok: false,
        message: data?.message || "Captcha is not configured.",
      };
    }
    return { ok: true, siteKey: data.siteKey };
  } catch (error) {
    return {
      ok: false,
      message:
        error instanceof Error ? error.message : "Captcha is not configured.",
    };
  }
}

/** Public contact form (email + captcha). Use on login and dashboard dialogs. */
export const sendGetInTouchPublic = async (
  payload: GetInTouchPayload
): Promise<GetInTouchResponse> => {
  try {
    const apiPath = resolveApiBaseUrl();
    if (!apiPath) {
      return { success: false, message: "API URL is not configured." };
    }

    const response = await fetch(`${apiPath}/contact`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: payload.email.trim(),
        turnstileToken: payload.turnstileToken,
        website: payload.website?.trim() || "",
        name: payload.name?.trim() || "",
        organization: payload.organization?.trim() || "",
        source: payload.source?.trim() || "labwick-site",
      }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const msg =
        data?.message || `Request failed: ${response.status} ${response.statusText}`;
      return { success: false, message: msg };
    }

    return { success: true, message: "Thanks — we will be in touch." };
  } catch (error) {
    console.error("Send get in touch failed:", error);
    return {
      success: false,
      message:
        error instanceof Error ? error.message : "Failed to send message.",
    };
  }
};

export const sendGetInTouch = sendGetInTouchPublic;
